import { useState, type FormEvent } from "react";

const SUBMIT_URL = "https://readdy.ai/api/form/da1baphst185ek8aso5g";

export default function Newsletter() {
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const data = new FormData(form);

    const honeypot = String(data.get('website_alt') || '').trim();
    if (honeypot) {
      setStatus('success');
      form.reset();
      return;
    }

    const payload = new URLSearchParams();
    data.forEach((value, key) => {
      if (key === 'website_alt') return;
      if (value instanceof File) {
        payload.append(key, 'Uncollectable');
      } else {
        payload.append(key, String(value));
      }
    });

    setStatus('loading');
    setFormError('');
    try {
      const res = await fetch(SUBMIT_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: payload.toString(),
      });
      const responseText = await res.text();
      let parsed: any = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '';
      const okCode = parsed?.code === 'OK' || parsed?.code === 'SUCCESS';

      if (res.ok && okCode && !/spam/i.test(String(serverMsg))) {
        setStatus('success');
        form.reset();
      } else {
        setStatus('error');
        setFormError(
          /spam/i.test(String(serverMsg)) ? '提交未通过校验，请重试。' : String(serverMsg) || '提交失败，请稍后再试。',
        );
      }
    } catch {
      setStatus('error');
      setFormError('网络异常，提交失败，请稍后再试。');
    }
  };

  return (
    <section id="subscribe" className="relative overflow-hidden bg-background-950 py-14 md:py-20">
      <div className="absolute inset-0 opacity-15">
        <img
          src="https://readdy.ai/api/search-image?query=wide%20abstract%20warm%20orange%20to%20dark%20gradient%20with%20subtle%20glowing%20film%20reel%20and%20star%20particles%2C%20minimal%20cinematic%20texture%20background&width=1600&height=700&seq=subscribe-bg&orientation=landscape"
          alt="订阅补番清单背景装饰"
          title="订阅小虎看番补番清单"
          className="h-full w-full object-cover"
        />
      </div>

      <div className="relative mx-auto max-w-3xl px-4 text-center md:px-6">
        <span className="inline-flex items-center gap-1.5 rounded-full bg-accent-500 px-3 py-1 text-xs font-bold uppercase tracking-wider text-background-50">
          <i className="ri-mail-send-fill" />
          每周片单直送
        </span>
        <h2 className="mt-4 font-heading text-2xl text-background-50 md:text-4xl">订阅小虎的补番清单</h2>
        <p className="mx-auto mt-3 max-w-xl text-sm leading-6 text-background-300 md:text-base">
          每周一封邮件，把小虎当周精选的补番推荐送到你手里。高分番、冷门佳作、完结番一次打包，再也不怕片荒。
        </p>

        {status === 'success' ? (
          <div className="mx-auto mt-8 max-w-md rounded-lg border border-accent-500/40 bg-accent-500/10 px-5 py-5">
            <i className="ri-checkbox-circle-fill text-3xl text-accent-400" />
            <p className="mt-2 text-base font-medium text-background-50">订阅成功！</p>
            <p className="mt-1 text-sm text-background-300">下一期补番清单正在路上，记得查收邮箱。</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} data-readdy-form className="mx-auto mt-8 max-w-xl">
            <div className="flex flex-col gap-3 sm:flex-row">
              <label htmlFor="email" className="sr-only">
                你的邮箱
              </label>
              <input
                id="email"
                name="email"
                type="email"
                required
                autoComplete="email"
                placeholder="请输入你的邮箱，例如 you@example.com"
                className="w-full flex-1 rounded-full border border-background-800 bg-background-900/60 px-5 py-3 text-sm text-background-50 placeholder:text-background-500 outline-none transition-colors focus:border-primary-500 focus:ring-2 focus:ring-primary-500/30"
              />
              <button
                type="submit"
                disabled={status === 'loading'}
                className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full bg-primary-500 px-6 py-3 text-sm font-bold text-background-50 hover:bg-primary-600 transition-colors disabled:opacity-60"
              >
                {status === 'loading' ? (
                  <>
                    <i className="ri-loader-4-line animate-spin" />
                    提交中
                  </>
                ) : (
                  <>
                    <i className="ri-mail-add-line" />
                    订阅补番清单
                  </>
                )}
              </button>
            </div>
            <input
              type="text"
              name="website_alt"
              className="website_alt"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              readOnly
            />
            {status === 'error' && (
              <p className="mt-3 rounded-full bg-primary-500/15 px-4 py-2 text-sm text-primary-300">{formError}</p>
            )}
          </form>
        )}
      </div>
    </section>
  );
}